import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { spinner } from '@modules/spinner/spinner';
import { PostRequest, basePostRequest } from '@type/request.type';
import { ListWithPagination, TableData } from '@type/table-data.type';
import { UserType, UsersList } from '@type/user.type';
import { joinUrls } from '@utils/urls';
import { Observable, lastValueFrom, map, tap } from 'rxjs';

@Injectable()
export class UsersService {
  updateUsersTrigger = signal(Date.now());

  private http = inject(HttpClient);

  getUsersList(opts: PostRequest = basePostRequest, companyId?: number): Observable<TableData<UsersList>> {
    const { users, index, proxies } = environment.urls;
    const url = () => {
      if (companyId) return joinUrls(proxies, String(companyId), users, index);
      return joinUrls(users, index);
    };

    return this.http
      .post<ListWithPagination<{ users: UsersList }>>(url(), opts)
      .pipe(map(({ meta, users }) => ({ length: meta.total_count, data: users })));
  }

  @spinner({ selectorById: 'spinner-container' })
  getUserById(userId: number, companyId?: number): Observable<UserType> {
    const { users, proxies } = environment.urls;
    const url = companyId
      ? joinUrls(proxies, String(companyId), users, String(userId))
      : joinUrls(users, String(userId));
    return this.http.get<UserType>(url);
  }

  @spinner({ selectorById: 'spinner-container' })
  createUser(opts: any, companyId?: number): Observable<UserType> {
    const { users, proxies } = environment.urls;

    const url = () => {
      if (companyId) return joinUrls(proxies, String(companyId), users);
      return joinUrls(users);
    };

    return this.http.post<UserType>(url(), opts).pipe(tap((res) => this.createInvitationUrl(res, companyId)));
  }

  @spinner({ selectorById: 'spinner-container' })
  updateUser(user: any, companyId?: number): Observable<UserType> {
    const { users, proxies } = environment.urls;
    const url = () => {
      if (companyId) return joinUrls(proxies, String(companyId), users, String(user['id']));
      return joinUrls(users, String(user['id']));
    };

    return this.http.put<UserType>(url(), user).pipe(
      tap((res) => {
        if (res.status.status === 'invited') {
          this.createInvitationUrl(res, companyId);
        }
        this.updateUsersTrigger.set(Date.now());
      }),
    );
  }

  @spinner({ selectorById: 'spinner-container' })
  updateCurrentUser(user: { first_name: string; last_name: string; id: number }): Observable<UserType> {
    const { users } = environment.urls;
    return this.http.put<UserType>(joinUrls(users, String(user.id)), user);
  }

  @spinner({ selectorById: 'spinner-container' })
  invateUser(user: { email: string; id: string | number }, companyId?: number): Observable<{ invitation_url: string }> {
    const { proxies, invitation, users, invite } = environment.urls;
    const url = () => {
      if (companyId) return joinUrls(proxies, String(companyId), invitation);
      return joinUrls(users, String(user['id']), invite);
    };

    return this.http
      .post<{ invitation_url: string }>(url(), user)
      .pipe(tap(() => this.updateUsersTrigger.set(Date.now())));
  }

  async createInvitationUrl(user: UserType, companyId?: number) {
    await lastValueFrom(
      this.invateUser(
        {
          email: user.email,
          id: user.id,
        },
        companyId,
      ),
    );
  }

  @spinner({ selectorById: 'spinner-container' })
  suspendUser(user: UserType, method: string, companyId?: number): Observable<UserType> {
    const { users, proxies } = environment.urls;
    const url = () => {
      if (companyId) return joinUrls(proxies, String(companyId), users, String(user['id']), method);
      return joinUrls(users, String(user['id']), method);
    };

    return this.http.post<UserType>(url(), '').pipe(
      tap((res) => {
        if (method === 'unblock' && !user.last_sign_in_at) {
          this.createInvitationUrl(res, companyId);
        }
        this.updateUsersTrigger.set(Date.now());
      }),
    );
  }

  @spinner({ selectorById: 'spinner-container' })
  resetPassword(user: UserType, companyId?: number): Observable<UserType> {
    const { users, proxies, invite } = environment.urls;
    const url = () => {
      if (companyId) return joinUrls(proxies, String(companyId), users, String(user['id']), invite);
      return joinUrls(users, String(user['id']), invite);
    };

    return this.http.post<UserType>(url(), '').pipe(tap(() => this.updateUsersTrigger.set(Date.now())));
  }

  createUsersRequest(opts: Partial<PostRequest>): PostRequest {
    return Object.assign(basePostRequest, opts);
  }

  copyUserSettings(
    companyId: number,
    donorId: number,
    recipientIds: number[],
  ): Observable<{ succeeded_count: number; failed_count: number; failed_recipient_ids: number[] }> {
    const { proxies, user_settings_copy } = environment.urls;
    return this.http.post<{ succeeded_count: number; failed_count: number; failed_recipient_ids: number[] }>(
      joinUrls(proxies, String(companyId), user_settings_copy),
      { donor_id: donorId, recipient_ids: recipientIds },
    );
  }
}
